// A1 Projects — card / list view, search. Shows only projects the user is a member of
// (super admin: all). Cards show project info + Latest update (date only); no module list (03/Oct/26).
// Round 13 (04/Oct/26):
//   #1 every list header sorts — click: ↑, again: ↓, third click: back to the default (newest update first).
//      Codes / members sort as numbers, dates as dates, My role by rank. Cards follow the same order.
//      The sort is remembered on this PC.
//   Round 14 #2: fixed column widths and a fixed-width arrow slot — sorting never moves the columns.
//   #8 ↑ / ↓ move through the list (cards view: ↑ ↓ ← →); Enter opens the project. Not while typing.
// Round 16 (v0.3.1): ★ stars (personal; assigned projects only) keep projects on top; a Project head also sees
//   projects they are not on — listed after theirs, tagged "Not assigned", view only, no star.
// v0.6.0 round 23 (A1.3): demo projects (BIM-991/992, + 993 since v0.7.2; 0014_demo.sql) show "(Demo)" after the code and are listed last;
//   only the root super admin sees real and demo together ("N projects · 2 demo").
import { useEffect, useMemo, useState } from "react";
import { useRpc } from "../api/useRpc";
import { rpc } from "../api/backend";
import { href, navigate } from "../router";
import { formatDate } from "../format";
import { useListKeys } from "../useListKeys";
import { nextSort, roleRank, sortArrow, sortBy, type SortState } from "../lists";
import { CodeChip, ErrorBox, InfoIcon, Loading, PageHeader, SearchBox, Seg, SortTh } from "../ui/kit";
import type { ProjectListItem } from "../api/types";
import { Icon } from "../ui/icons";

type Key = "code" | "name" | "client" | "members" | "role" | "created" | "latest";
const GET: Record<Key, (p: ProjectListItem) => string | number> = {
  code: (p) => p.code, name: (p) => p.name, client: (p) => p.client, members: (p) => p.member_count,
  role: (p) => roleRank(p.my_role), created: (p) => p.created_at, latest: (p) => p.latest_update,
};
const COLS: [Key, string, string?][] = [["code", "Code"], ["name", "Project"], ["client", "Client"], ["members", "Members", "num"],
  ["role", "My role"], ["created", "Created"], ["latest", "Latest update"]];
const SORT_KEY = "buildnexx-a1-sort";
const VIEW_KEY = "buildnexx-a1-view";
const CARD_COLS = 3;

/** Default order: newest Latest update first, then code. Round 16: starred first, then assigned, then "Not assigned";
 *  the chosen sort applies inside each group. */
export function sortProjects(rows: ProjectListItem[], s: SortState<Key>): ProjectListItem[] {
  const inner = !s.key
    ? [...rows].sort((a, b) => b.latest_update.localeCompare(a.latest_update) || a.code.localeCompare(b.code))
    : sortBy(rows, GET[s.key], s.dir);
  const group = (p: ProjectListItem) => (p.is_demo ? 3 : p.starred ? 0 : p.assigned === false ? 2 : 1);
  return inner.map((p, i) => ({ p, i })).sort((a, b) => group(a.p) - group(b.p) || a.i - b.i).map((x) => x.p);
}

const DEMO_TIP = ["Demo projects (BIM-991, BIM-992, BIM-993) are for trying things out and for acceptance checks.",
  "You and the demo logins see them; your staff never do — and demo logins never see real projects.",
  "They are listed after your real projects; the code shows (Demo).",
  "Load or remove them from GitHub › Actions › Demo data (Admin console › App settings links there)."];
const DemoTag = ({ p }: { p: ProjectListItem }) => (p.is_demo ? <span className="demo-tag" data-demo="1">(Demo)</span> : null);

/** ★ toggle (or the "Not assigned" tag where starring is not allowed). */
function Star({ p, onToggle }: { p: ProjectListItem; onToggle: (p: ProjectListItem) => void }) {
  if (p.assigned === false) return <span className="pill p-unassigned" data-testid="not-assigned">Not assigned</span>;
  return <button type="button" className={`star-btn${p.starred ? " on" : ""}`} aria-pressed={p.starred} aria-label={`${p.starred ? "Unstar" : "Star"} ${p.code}`}
    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(p); }}><Icon name={p.starred ? "star" : "star_border"} /></button>;
}

export function ProjectsPage() {
  const { data, error, loading, reload } = useRpc("list_projects");
  const [stars, setStars] = useState<Record<string, boolean>>({});      // optimistic ★ until the list reloads
  const list = useMemo(() => (data ?? []).map((p) => (p.code in stars ? { ...p, starred: stars[p.code] } : p)), [data, stars]);
  const toggle = (p: ProjectListItem) => {
    setStars((s) => ({ ...s, [p.code]: !p.starred }));
    rpc("star_project", { code: p.code, on: !p.starred }).then(reload).catch(() => setStars((s) => { const n = { ...s }; delete n[p.code]; return n; }));
  };
  // round 18 #6: Cards / List is remembered on this PC, like the sort
  const [view, setView] = useState<"cards" | "list">(() => { try { return localStorage.getItem(VIEW_KEY) === "list" ? "list" : "cards"; } catch { return "cards"; } });
  useEffect(() => { try { localStorage.setItem(VIEW_KEY, view); } catch { /* private window */ } }, [view]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<SortState<Key>>(() => { try { return JSON.parse(localStorage.getItem(SORT_KEY) ?? "") ?? { key: null, dir: "asc" }; } catch { return { key: null, dir: "asc" }; } });
  const [hi, setHi] = useState<number | null>(null);
  useEffect(() => { try { localStorage.setItem(SORT_KEY, JSON.stringify(sort)); } catch { /* private window */ } }, [sort]);
  const rows = useMemo(() => sortProjects(list.filter((p) => `${p.name} ${p.code} ${p.client}`.toLowerCase().includes(q.trim().toLowerCase())), sort), [list, q, sort]);
  useEffect(() => setHi(null), [q, view, sort]);
  useListKeys({ count: rows.length, hi, setHi, cols: view === "cards" ? CARD_COLS : undefined, onEnter: (i) => navigate({ name: "dashboard", code: rows[i].code }) });
  const demoCount = list.filter((p) => p.is_demo).length;
  const mixed = demoCount > 0 && demoCount < list.length;         // root super admin with demo data loaded
  return (
    <>
      <PageHeader crumb="BuildNexx" title="Projects"
        sub={mixed ? <>{list.length} projects · {demoCount} demo <InfoIcon tip={DEMO_TIP} /></> :
          <>{data?.length ?? 0} projects{list.some((p) => p.assigned === false) && <> · {list.filter((p) => p.assigned !== false).length} assigned</>}{" "}
          <InfoIcon tip={["You see the projects you are assigned to.", "GM and the super admin see every project.", "A project head also sees the other projects, view only — tagged Not assigned, listed after yours.", "☆ stars a project: starred projects stay at the top (here and in the project switcher). Your stars are personal and follow you to any PC."]} /></>}
        actions={<Seg label="View" value={view} onChange={setView} options={[{ value: "cards", label: <><Icon name="grid_view" /> Cards</> }, { value: "list", label: <><Icon name="view_list" /> List</> }]} />}
        right={<SearchBox placeholder="Search projects" value={q} onChange={setQ} width={260} />} />
      {loading && !data && <Loading />}{/* first load only — a reload (e.g. after ★) never pushes the cards down */}
      {error && <ErrorBox message={error} />}
      {view === "cards" ? (
        <div className="card-grid">
          {rows.map((p, k) => (
            <a key={p.id} data-row={k} className={`project-card${k === hi ? " kbd" : ""}`} href={href({ name: "dashboard", code: p.code })}>
              <div className="row"><CodeChip code={p.code} /><DemoTag p={p} /><b className="ellipsis title grow">{p.name}</b><Star p={p} onToggle={toggle} /></div>
              <p className="muted ellipsis">{p.client}</p>
              <div className="row between bottom small">
                <span>Latest update · <b className="date">{formatDate(p.latest_update)}</b></span>
                <span className="muted">{p.member_count} {p.member_count === 1 ? "member" : "members"}</span>
              </div>
            </a>
          ))}
        </div>
      ) : (
        <div className="card list-card">
          <table className="fixed-cols">
            <colgroup><col style={{ width: 24 }} /><col style={{ width: 96 }} /><col /><col style={{ width: 220 }} /><col style={{ width: 92 }} /><col style={{ width: 116 }} /><col style={{ width: 96 }} /><col style={{ width: 124 }} /></colgroup>
            <thead><tr>
              <th className="star-col" aria-label="Starred" />
              {COLS.map(([k, label, cls]) => <SortTh key={k} label={label} className={cls} arrow={sortArrow(sort, k)} onClick={() => setSort(nextSort(sort, k))} />)}
              {/* round 23 comment #2 (Kyle 18/Oct/26): no (i) at the end of the list header */}
            </tr></thead>
            <tbody>{rows.map((p, k) => (
              <tr key={p.id} data-row={k} className={`clickable${k === hi ? " kbd" : ""}`} onClick={() => navigate({ name: "dashboard", code: p.code })}>
                <td className="star-col">{p.assigned === false ? null : <Star p={p} onToggle={toggle} />}</td>
                <td><CodeChip code={p.code} />{p.is_demo && <> <DemoTag p={p} /></>}</td><td>{p.name}{p.assigned === false && <> <span className="pill p-unassigned">Not assigned</span></>}</td><td>{p.client}</td>
                <td className="num">{p.member_count}</td><td>{p.my_role ?? "–"}</td>
                <td className="nowrap">{formatDate(p.created_at)}</td><td className="nowrap">{formatDate(p.latest_update)}</td>
              </tr>))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
